import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { SEOHead } from '@/components/atoms/SEOHead';
import {
  PatternImg,
  DirBadge,
  dirFromCat,
  patternAnchor,
  SuccessRateBar,
} from '@/components/patterns/patternGraphics';
import {
  CHART_PATTERNS,
  CHART_PATTERN_CATEGORIES,
  type ChartPatternCat,
  type ChartPatternDef,
} from '@/data/chartPatterns';

/* ── 차트 패턴 사전 ─────────────────────────────────────────
 *  신규 SVG 세트(87종, gemini-code extracted)를 사용.
 *  도식은 public/images/patterns/{file}.svg 를 <img> 로 참조(원본 다크 도식 유지).
 *  각 카드 id = patternAnchor(file)(숫자·카테고리 접두어 제거 슬러그) → 차트분석 탭 패턴명 클릭 시 동일 anchor 로 연결. */

type TabKey = '전체' | ChartPatternCat;
const TABS: TabKey[] = ['전체', ...CHART_PATTERN_CATEGORIES];

/** 패턴 카드 — flex column 레이아웃.
 *  설명(desc)이 남은 공간을 차지(flex-1)해 통계기반 달성률·태그가 항상 카드 하단 같은 위치에 고정.
 *  grid item 의 align stretch(기본값) 덕분에 같은 행 카드들이 같은 높이로 정렬됨. */
function PatternDictCard({ p }: { p: ChartPatternDef }) {
  const dir = dirFromCat(p.cat, p.tags);
  return (
    <li
      id={patternAnchor(p.file)}
      className="flex h-full scroll-mt-24 flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]"
    >
      {/* 도식 SVG (원본 다크 카드) — 고정 영역 */}
      <PatternImg file={p.file} name={p.name} className="block h-auto w-full shrink-0" />
      {/* 내용 — flex column. desc 가 grow, 통계기반 달성률/태그가 하단 고정 */}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-bold text-[var(--color-text)]">{p.name}</h3>
          <DirBadge dir={dir} />
        </div>
        {/* 설명 — 남은 공간 차지 */}
        <p className="mt-2 flex-1 text-xs leading-relaxed text-[var(--color-text-sub)]">{p.desc}</p>
        {/* 통계기반 달성률(없으면 "달성률 데이터 없음") — 카드 하단 고정 */}
        <SuccessRateBar rate={p.successRate} className="mt-3 shrink-0" />
        {p.tags.length > 0 && (
          <div className="mt-2.5 flex shrink-0 flex-wrap gap-1">
            {p.tags.map((t) => (
              <span
                key={t}
                className="rounded-md bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-text-sub)]"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

export function ChartPatternsPage() {
  const [tab, setTab] = useState<TabKey>('전체');
  const { hash } = useLocation();

  const list = useMemo(
    () => (tab === '전체' ? CHART_PATTERNS : CHART_PATTERNS.filter((p) => p.cat === tab)),
    [tab],
  );

  /* #anchor 진입 시: 해당 패턴이 보이도록 '전체' 탭으로 맞추고 스크롤.
   *  새 탭 전체 로드에서는 네이티브 스크롤이 렌더 전에 빗나갈 수 있어 직접 처리. */
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;
    setTab('전체');
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="container-base py-10">
      <SEOHead
        title="차트 패턴 사전"
        description="헤드앤숄더·더블탑·삼각수렴·깃발·갭·캔들·하모닉 등 주요 차트 패턴 87종의 도식과 의미를 카테고리별로 정리했어요."
        canonical="/chart-patterns"
      />

      {/* 헤더 */}
      <h1 className="mb-1 text-2xl font-extrabold text-[var(--color-text)]">차트 패턴 사전</h1>
      <p className="mb-6 text-sm text-[var(--color-text-sub)]">
        주요 차트 패턴과 예상 시나리오를 확인하세요
      </p>

      {/* 카테고리 탭 */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((c) => (
          <button
            key={c}
            onClick={() => setTab(c)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
              tab === c
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]',
            )}
          >
            {c}
            <span className="ml-1 text-[11px] opacity-70">
              {c === '전체'
                ? CHART_PATTERNS.length
                : CHART_PATTERNS.filter((p) => p.cat === c).length}
            </span>
          </button>
        ))}
      </div>

      {/* 패턴 목록 */}
      <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => (
          <PatternDictCard key={p.file} p={p} />
        ))}
      </ul>

      {/* 통계기반 달성률 출처 안내 + 면책 */}
      <p className="mt-8 text-center text-xs leading-relaxed text-[var(--color-text-sub)]">
        ※ 통계기반 달성률은 Thomas Bulkowski의 차트 패턴 백테스트 통계 기반이며, 실제 수익을 보장하지 않습니다.
      </p>
      <p className="mt-1 text-center text-xs text-[var(--color-text-sub)]">
        차트 패턴은 과거 흐름을 바탕으로 한 참고 자료이며, 미래 수익을 보장하지 않습니다. 투자 판단과 책임은 본인에게 있습니다.
      </p>
    </div>
  );
}
